import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Grid,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

// Interfaces
interface Event {
  id?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  isVirtual?: boolean;
  meetingLink?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeTripDetails: boolean;
}

interface EmailTemplateStepProps {
  template: EmailTemplate;
  onChange: (template: EmailTemplate) => void;
  selectedEvent: Event | null;
  selectedContacts: Contact[];
}

const EmailTemplateStep: React.FC<EmailTemplateStepProps> = ({
  template,
  onChange,
  selectedEvent,
  selectedContacts,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewContact, setPreviewContact] = useState<Contact | null>(
    selectedContacts.length > 0 ? selectedContacts[0] : null
  );

  // Helper function to get event date from various possible field names
  const getEventDate = (event: any) => {
    if (!event) return '';
    return event.eventDate || event.date || event.startDate || event.start_date || event.event_date || '';
  };

  // Format date for display
  function formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  // Available templates
  const emailTemplates = [
    {
      id: 'default',
      name: 'Default Invitation',
      subject: `Invitation: ${selectedEvent?.eventName}`,
      body: `
Dear {{firstName}} {{lastName}},

You are cordially invited to attend ${selectedEvent?.eventName} on ${formatDate(getEventDate(selectedEvent))}.

Event Details:
- Date: ${formatDate(getEventDate(selectedEvent))}
- Time: ${selectedEvent?.startTime || '(TBD)'} - ${selectedEvent?.endTime || '(TBD)'}
- Location: ${selectedEvent?.location || '(TBD)'}
${selectedEvent?.isVirtual ? `- Meeting Link: ${selectedEvent?.meetingLink || '(will be provided closer to the event)'}` : ''}

Description:
${selectedEvent?.eventDescription || 'Join us for this exciting event!'}

We hope to see you there!

Best regards,
{{organizationName}} Team
      `
    },
    {
      id: 'formal',
      name: 'Formal Invitation',
      subject: `Formal Invitation: ${selectedEvent?.eventName}`,
      body: `
Dear {{firstName}} {{lastName}},

On behalf of {{organizationName}}, we are pleased to extend a formal invitation for you to join us at ${selectedEvent?.eventName}.

The event will take place on ${formatDate(getEventDate(selectedEvent))}, from ${selectedEvent?.startTime || '(TBD)'} to ${selectedEvent?.endTime || '(TBD)'} at ${selectedEvent?.location || '(location to be announced)'}.

${selectedEvent?.eventDescription ? `About the event: ${selectedEvent.eventDescription}` : ''}

Your presence would be greatly appreciated. Please RSVP by clicking the link below.

Sincerely,
{{organizationName}} Events Team
      `
    },
    {
      id: 'casual',
      name: 'Casual Invitation',
      subject: `Join us for ${selectedEvent?.eventName}!`,
      body: `
Hi {{firstName}} {{lastName}}!

We'd love to have you join us for ${selectedEvent?.eventName}!

When? ${formatDate(getEventDate(selectedEvent))} at ${selectedEvent?.startTime || '(time to be announced)'}
Where? ${selectedEvent?.location || '(location to be announced)'}
${selectedEvent?.isVirtual ? `Meeting Link: ${selectedEvent?.meetingLink || '(will be sent later)'}` : ''}

${selectedEvent?.eventDescription || 'It\'s going to be great and we hope you can make it!'}

Hope to see you there!
The {{organizationName}} Team
      `
    }
  ];

  // Handle template change
  const handleTemplateChange = (field: keyof EmailTemplate, value: string | boolean) => {
    onChange({
      ...template,
      [field]: value
    });
  };

  // Handle loading a template
  const handleLoadTemplate = (templateId: string) => {
    const selectedTemplate = emailTemplates.find(t => t.id === templateId);
    if (selectedTemplate) {
      onChange({
        ...template,
        subject: selectedTemplate.subject,
        body: selectedTemplate.body,
      });
    }
  };

  // Replace template variables for preview
  const renderPreview = (text: string): string => {
    if (!previewContact) return text;
    
    return text
      .replace(/\{\{firstName\}\}/g, previewContact.firstName)
      .replace(/\{\{lastName\}\}/g, previewContact.lastName)
      .replace(/\{\{email\}\}/g, previewContact.email)
      .replace(/\{\{organizationName\}\}/g, 'Pangia Travel');
  };

  // Insert template variable at cursor position
  const insertVariable = (variable: string) => {
    const textField = document.getElementById('email-body') as HTMLTextAreaElement;
    if (!textField) return;
    
    const startPos = textField.selectionStart;
    const endPos = textField.selectionEnd;
    
    const newBody = 
      template.body.substring(0, startPos) + 
      `{{${variable}}}` + 
      template.body.substring(endPos);
    
    handleTemplateChange('body', newBody);
    
    // Focus back on the field
    setTimeout(() => {
      textField.focus();
      textField.setSelectionRange(startPos + variable.length + 4, startPos + variable.length + 4);
    }, 0);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Customize Email Invitation
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Personalize the email that will be sent to your contacts. Use variables like {'{{'} firstName {'}}'} to personalize the message.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={!showPreview ? 12 : 6}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="template-select-label">Load Template</InputLabel>
              <Select
                labelId="template-select-label"
                label="Load Template"
                defaultValue=""
                onChange={(e) => handleLoadTemplate(e.target.value)}
              >
                <MenuItem value="" disabled><em>Select a Template</em></MenuItem>
                {emailTemplates.map(template => (
                  <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Email Subject"
            fullWidth
            value={template.subject}
            onChange={(e) => handleTemplateChange('subject', e.target.value)}
            margin="normal"
          />

          <Box sx={{ mb: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">Email Body</Typography>
              <Box>
                <Tooltip title="Add Bold Text">
                  <IconButton 
                    size="small"
                    onClick={() => insertVariable('**text**')}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add Italic Text">
                  <IconButton 
                    size="small"
                    onClick={() => insertVariable('*text*')}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add Link">
                  <IconButton 
                    size="small"
                    onClick={() => insertVariable('[text](url)')}
                  >
                    <InsertLinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <TextField
            id="email-body"
            multiline
            rows={12}
            fullWidth
            value={template.body}
            onChange={(e) => handleTemplateChange('body', e.target.value)}
            placeholder="Write your email content here..."
            variant="outlined"
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Available Variables:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label="firstName" 
                size="small" 
                onClick={() => insertVariable('firstName')}
              />
              <Chip 
                label="lastName" 
                size="small" 
                onClick={() => insertVariable('lastName')}
              />
              <Chip 
                label="email" 
                size="small" 
                onClick={() => insertVariable('email')}
              />
              <Chip 
                label="organizationName" 
                size="small" 
                onClick={() => insertVariable('organizationName')}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={template.includeCalendarInvite}
                  onChange={(e) => handleTemplateChange('includeCalendarInvite', e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">Include Calendar Invite</Typography>
                  <Tooltip title="Adds an .ics file attachment to the email">
                    <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Tooltip>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={template.includeTripDetails}
                  onChange={(e) => handleTemplateChange('includeTripDetails', e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">Include Trip Details (if applicable)</Typography>
                  <Tooltip title="Includes related trip information if this event is part of a trip">
                    <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        </Grid>

        {showPreview && (
          <Grid item xs={12} md={6}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom>
                Email Preview
                {previewContact && (
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    (for {previewContact.firstName} {previewContact.lastName})
                  </Typography>
                )}
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject:
                </Typography>
                <Typography variant="body1">
                  {renderPreview(template.subject)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Body:
                </Typography>
                <Box 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    p: 2,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 1,
                    mt: 1,
                    minHeight: 300
                  }}
                >
                  {renderPreview(template.body)}
                </Box>
              </Box>
              
              {template.includeCalendarInvite && (
                <Chip 
                  label="Calendar Invite (.ics)" 
                  size="small" 
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="outlined" 
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </Button>
      </Box>
    </Box>
  );
};

export default EmailTemplateStep;
